/**
 * Chat utility functions for message creation and manipulation
 */

import { ChatMessage } from '../types/chat';

/**
 * Generate a unique message ID with optional prefix
 */
export const generateMessageId = (prefix: string = 'msg'): string => {
  return `${prefix}-${Date.now()}`;
};

/**
 * Create a user message object
 */
export const createUserMessage = (
  content: string,
  idPrefix: string = 'user'
): ChatMessage => ({
  id: generateMessageId(idPrefix),
  role: 'user',
  content,
  timestamp: new Date(),
});

/**
 * Create an assistant message object
 */
export const createAssistantMessage = (
  content: string,
  options: {
    idPrefix?: string;
    actions?: ChatMessage['actions'];
    criteria?: string[];
    animate?: boolean;
  } = {}
): ChatMessage => ({
  id: generateMessageId(options.idPrefix || 'assistant'),
  role: 'assistant',
  content,
  timestamp: new Date(),
  ...(options.actions && { actions: options.actions }),
  ...(options.criteria && { criteria: options.criteria }),
  ...(options.animate !== undefined && { animate: options.animate }),
});

/**
 * Create a pair of user and assistant messages (common for flow interactions)
 */
export const createMessagePair = (
  userContent: string,
  assistantContent: string,
  options: {
    userIdPrefix?: string;
    assistantIdPrefix?: string;
    assistantOptions?: Parameters<typeof createAssistantMessage>[1];
  } = {}
): [ChatMessage, ChatMessage] => {
  const timestamp = Date.now();
  return [
    {
      id: `${options.userIdPrefix || 'user'}-${timestamp}`,
      role: 'user',
      content: userContent,
      timestamp: new Date(timestamp),
    },
    {
      id: `${options.assistantIdPrefix || 'assistant'}-${timestamp + 1}`,
      role: 'assistant',
      content: assistantContent,
      timestamp: new Date(timestamp + 1),
      ...(options.assistantOptions?.actions && { actions: options.assistantOptions.actions }),
      ...(options.assistantOptions?.criteria && { criteria: options.assistantOptions.criteria }),
      ...(options.assistantOptions?.animate !== undefined && { animate: options.assistantOptions.animate }),
    },
  ];
};
